import React, { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';

import AllCandidates from './AllCandidates';
import RecentlyViewed from './RecentlyViewed';
import CandidatesListHeader from './CandidatesListHeader';
import IntelCandidate from './IntelCandidate';
import CandidateDetails from '../../CandidateDetails';

import useDebouncedSearchWithHistory from '../../../../hooks/useDebouncedSearchWithHistory';
import CandidatesService from '../../../../services/CandidatesService';
import classes from './styles.module.scss';
import { useUserStore } from '../../../../store';

export default function CandidatesList() {
  const isClient = useUserStore((state) => state.isClient);
  const companyId = useUserStore((state) => state.companyId);
  const [activeSortOption, setActiveSortOption] = useState({});
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [isFullScreenModeEnabled, setIsFullScreenModeEnabled] = useState(true);
  const [isCandidatePopupVisible, setIsCandidatePopupVisible] = useState(false);
  const [currentCandidate, setCurrentCandidate] = useState(null);
  const [view, setView] = useState('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const singleCandidateId = searchParams.get('candidate');

  const [isIntelCandidatePopupVisible, setIsIntelCandidatePopupVisible] =
    useState(false);

  const { debouncedSearchTerm, searchHistory } = useDebouncedSearchWithHistory(
    searchTerm,
    'candidates'
  );

  const {
    data: candidates,
    refetch: refetchCandidates,
    isFetching,
  } = useQuery({
    queryKey: [
      'candidates',
      activeSortOption,
      debouncedSearchTerm,
      sortOrder,
      currentPage,
      isClient,
      companyId,
    ],
    queryFn: () =>
      CandidatesService.getCandidatesV3({
        term: debouncedSearchTerm,
        orderBy: activeSortOption.value,
        sortOrder,
        pageNumber: currentPage,
        pageSize: 20,
        companyId: isClient ? companyId : null,
      }),
    keepPreviousData: true,
    staleTime: 60 * 1000,
    cacheTime: 60 * 1000,
    enabled: !isClient || companyId !== undefined,
  });

  const { data: viewedCandidatesData } = useQuery({
    queryKey: ['viewedCandidates', isClient, companyId],
    queryFn: () =>
      CandidatesService.getAllCandidates({
        companyId: isClient ? companyId : null,
        Filters: 'viewedAt:noteq:null',
      }),
    keepPreviousData: true,
    enabled: !isClient || companyId !== undefined,
  });

  const { data: singleCandidate } = useQuery({
    queryKey: ['candidate', singleCandidateId],
    queryFn: () => CandidatesService.getCandidate(singleCandidateId),
    enabled: !!singleCandidateId,
  });

  const toggleCandidateSelection = (candidateId) => {
    setSelectedRows((prevRows) => {
      if (prevRows.includes(candidateId)) {
        return prevRows.filter((row) => row !== candidateId);
      }
      return [...prevRows, candidateId];
    });
  };

  const recentlyViewedCandidates = viewedCandidatesData?.filter?.(
    (candidate) => candidate.viewedAt
  );

  const shoNewCandidatePopup = () => {
    setIsIntelCandidatePopupVisible(true);
  };

  useEffect(() => {
    if (singleCandidateId && singleCandidate) {
      setCurrentCandidate(singleCandidate);

      setIsCandidatePopupVisible(true);
    }
  }, [singleCandidate, singleCandidateId]);

  return (
    <div
      className={classNames(classes.CandidatesList, {
        [classes.tableView]: view === 'list',
        [classes.fullScreen]: isFullScreenModeEnabled,
      })}
    >
      <div className={classes.headerContainer}>
        <CandidatesListHeader
          totalCount={candidates?.data?.length}
          showAddCandidatePopup={shoNewCandidatePopup}
        />
      </div>
      {recentlyViewedCandidates?.length ? (
        <div
          className={classes.recentylViewedContainer}
          style={{ display: isFullScreenModeEnabled ? 'none' : 'block' }}
        >
          <RecentlyViewed
            candidates={recentlyViewedCandidates}
            setIsCandidatePopupVisible={setIsCandidatePopupVisible}
            setCurrentCandidate={setCurrentCandidate}
          />
        </div>
      ) : null}
      <div className={classes.allCandidatesContainer}>
        <div className={classes.innerContainer}>
          <AllCandidates
            debouncedSearchTerm={debouncedSearchTerm}
            showAddCandidatePopup={shoNewCandidatePopup}
            isFullScreenModeEnabled={isFullScreenModeEnabled}
            setIsFullScreenModeEnabled={setIsFullScreenModeEnabled}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
            // sortOptions={sortOptions}
            candidates={candidates?.data || []}
            refetch={refetchCandidates}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            toggleCandidateSelection={toggleCandidateSelection}
            activeSortOption={activeSortOption}
            setActiveSortOption={setActiveSortOption}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
            searchHistory={searchHistory}
            isFullScreenButtonVisible={!!recentlyViewedCandidates?.length}
            setIsCandidatePopupVisible={setIsCandidatePopupVisible}
            setCurrentCandidate={setCurrentCandidate}
            view={view}
            setView={setView}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={candidates?.totalPages}
            activeCandidateFilter="All"
            setActiveCandidateFilter={() => {}}
            candidatesCount={candidates?.totalCount}
            isFetching={isFetching}
          />
        </div>
      </div>
      {isIntelCandidatePopupVisible && (
        <IntelCandidate
          handleClose={() => {
            setIsIntelCandidatePopupVisible(false);
            setSelectedRows([]);
          }}
          refetchCandidates={refetchCandidates}
          candidate={
            candidates?.data?.filter((candidate) =>
              selectedRows.includes(candidate.id)
            )[0]?.careerInfo || null
          }
        />
      )}

      <CandidateDetails
        isVisible={isCandidatePopupVisible}
        setIsVisible={() => {
          setIsCandidatePopupVisible();
          setCurrentCandidate(null);
          setSearchParams({});
        }}
        refetch={refetchCandidates}
        candidate={currentCandidate}
        itemIds={
          singleCandidateId
            ? [singleCandidateId]
            : candidates?.data?.map((candidate) => candidate.id)
        }
        setCurrentItemId={(itemId) => {
          setCurrentCandidate(
            candidates?.data?.find((candidate) => candidate.id === itemId)
          );
        }}
      />
    </div>
  );
}
